/**
 * Cliente REST de la autoría por IA (B8). Datos SERVER-ONLY (sin PowerSync):
 * las propuestas son server-authoritative — el presupuesto, la credencial
 * sellada y el antifraude viven en el servidor, y sacarlas a sync las expondría
 * a un cliente modificado.
 *
 * Espejo de los DTO de `apps/server/src/handlers/course_author.rs`.
 */
import { createMutation, createQuery } from "@tanstack/solid-query";
import type {
  AiAuthoringAvailability,
  AiItemDecision,
  AiProposalCreated,
  AiProposalDetail,
  AiProposalSummary,
} from "@space/shared";
import { apiJson } from "@/lib/rest";

const BASE = "/api/course-author";

export interface ProposeInput {
  org?: string;
  prompt: string;
  /**
   * `CourseModuleRegistry.kinds()` ∩ `course_settings.enabled_module_kinds`.
   * Los manda el CLIENTE porque el registry es TypeScript: si el servidor los
   * cableara, registrar un plugin nuevo dejaría a la IA sin poder proponerlo.
   */
  allowedModuleKinds: string[];
  allowedBlockTypes: string[];
  courseId?: string;
  lessonId?: string;
  courseTitle?: string;
  lessonTitle?: string;
}

export interface ProposeConfigInput {
  org?: string;
  prompt: string;
  /** `rubric` | `session_plan`. */
  configKind: string;
  courseId?: string;
}

const keys = {
  availability: (org?: string, courseId?: string) =>
    ["ai-authoring", "availability", org ?? "", courseId ?? ""] as const,
  proposals: (org?: string, courseId?: string) =>
    ["ai-authoring", "proposals", org ?? "", courseId ?? ""] as const,
  proposal: (id: string) => ["ai-authoring", "proposal", id] as const,
};

export const useAiAuthoringAvailability = (
  org: () => string | undefined,
  courseId: () => string | undefined,
) =>
  createQuery(() => ({
    queryKey: keys.availability(org(), courseId()),
    queryFn: () => {
      const params = new URLSearchParams();
      const o = org();
      if (o) {
        params.set("org", o);
      }
      const c = courseId();
      if (c) {
        params.set("courseId", c);
      }
      const qs = params.toString();
      return apiJson<AiAuthoringAvailability>(`${BASE}/availability${qs ? `?${qs}` : ""}`);
    },
    staleTime: 30_000,
    retry: false,
  }));

export const useAiProposals = (org: () => string | undefined, courseId: () => string | undefined) =>
  createQuery(() => ({
    queryKey: keys.proposals(org(), courseId()),
    queryFn: () => {
      const params = new URLSearchParams();
      const o = org();
      if (o) {
        params.set("org", o);
      }
      const c = courseId();
      if (c) {
        params.set("courseId", c);
      }
      const qs = params.toString();
      return apiJson<AiProposalSummary[]>(`${BASE}/proposals${qs ? `?${qs}` : ""}`);
    },
    staleTime: 5000,
    retry: false,
  }));

export const useAiProposal = (id: () => string | undefined) =>
  createQuery(() => ({
    queryKey: keys.proposal(id() ?? ""),
    queryFn: () => apiJson<AiProposalDetail>(`${BASE}/proposals/${id() ?? ""}`),
    get enabled() {
      return Boolean(id());
    },
    retry: false,
  }));

const post = <T>(path: string, body: unknown) =>
  apiJson<T>(`${BASE}${path}`, { method: "POST", body: JSON.stringify(body) });

export const useProposeStructure = () =>
  createMutation(() => ({
    mutationFn: (input: ProposeInput) => post<AiProposalCreated>("/propose/structure", input),
  }));

export const useProposeBlocks = () =>
  createMutation(() => ({
    mutationFn: (input: ProposeInput) => post<AiProposalCreated>("/propose/blocks", input),
  }));

export const useProposeEdit = () =>
  createMutation(() => ({
    mutationFn: (input: ProposeInput) => post<AiProposalCreated>("/propose/edit", input),
  }));

export const useProposeConfig = () =>
  createMutation(() => ({
    mutationFn: (input: ProposeConfigInput) => post<AiProposalCreated>("/propose/config", input),
  }));

/**
 * Registra las decisiones del revisor. El servidor vuelve a comprobar `valid`:
 * este veredicto lo produce `validateProposalItem` cruzando los dos registros,
 * pero la barrera de verdad está en SQL (`AND valid`), no en esta llamada.
 */
export const useDecideItems = () =>
  createMutation(() => ({
    mutationFn: (input: { proposalId: string; decisions: AiItemDecision[] }) =>
      post<{ changed: number; status: string }>(`/proposals/${input.proposalId}/decide`, {
        decisions: input.decisions,
      }),
  }));

export const useDeleteProposal = () =>
  createMutation(() => ({
    mutationFn: (id: string) =>
      apiJson<{ ok: boolean }>(`${BASE}/proposals/${id}`, { method: "DELETE" }),
  }));
